// src/runtime/AgentRuntime/ceo/types.ts
// ============================================
// CEO Agent - 类型定义（Sprint 2 Task 4）
//
// 职责：
//   1. 定义 AI CEO 日报的完整类型体系
//   2. 涵盖执行摘要 / 收入 / 增长 / 内容 / 客户 / 风险 / Agent / Workflow / 建议 / 次日计划
//   3. 提供查询/命令枚举（CEOQuery / CEOCommand）
//
// 设计原则：
//   - 真分析（不是统计）：每个字段都为深度洞察服务
//   - 真建议（可执行）：建议必须含优先级 / 预期收益 / 工作量 / 时间线 / 行动项
//   - 数据收集失败的字段允许空值，由 Analyzer 降级填充
//   - TypeScript 严格模式，所有可选字段显式标注
// ============================================

// ============================================
// CEO 日报章节枚举
// ============================================

/**
 * CEO 日报章节
 *
 * 日报按这 10 个章节组织，对应 CEOReport 的字段
 */
export enum CEOReportSection {
  /** 执行摘要 */
  Executive = 'executive',
  /** 收入分析 */
  Revenue = 'revenue',
  /** 增长分析 */
  Growth = 'growth',
  /** 内容分析 */
  Content = 'content',
  /** 客户分析 */
  Customer = 'customer',
  /** 风险预警 */
  Risk = 'risk',
  /** Agent 表现 */
  AgentPerformance = 'agent_performance',
  /** Workflow 表现 */
  WorkflowPerformance = 'workflow_performance',
  /** 可执行建议 */
  Recommendations = 'recommendations',
  /** 次日计划 */
  NextDay = 'next_day',
}

// ============================================
// 执行摘要
// ============================================

/**
 * 执行摘要
 *
 * 给 CEO 一眼看清当日全局的核心信息
 */
export interface ExecutiveSummary {
  /** 当日头条（一句话总结当日表现） */
  headline: string;
  /** 核心指标快照 */
  keyMetrics: {
    /** 当日收入（元） */
    revenue: number;
    /** 当日新客户数 */
    newCustomers: number;
    /** 当日发布内容数 */
    contentPublished: number;
    /** 活跃工作流数 */
    activeWorkflows: number;
  };
  /** 亮点（当日做得好的事） */
  highlights: string[];
  /** 隐患（当日需要关注的问题） */
  concerns: string[];
}

// ============================================
// 收入分析
// ============================================

/**
 * 收入分析
 *
 * 不只是数字，更要分析趋势和预测
 */
export interface RevenueAnalysis {
  /** 当日收入（元） */
  todayRevenue: number;
  /** 本周累计收入 */
  weekToDate: number;
  /** 本月累计收入 */
  monthToDate: number;
  /** 趋势：上升 / 下降 / 持平 */
  trend: 'up' | 'down' | 'flat';
  /** 变化率（百分比，如 12.5 表示 +12.5%） */
  changeRate: number;
  /** Top 产品（按收入排序） */
  topProducts: {
    /** SKU 编码 */
    sku: string;
    /** 该 SKU 收入 */
    revenue: number;
    /** 销售件数 */
    units: number;
  }[];
  /** 按渠道汇总收入 */
  revenueByChannel: Record<string, number>;
  /** 未来 7 天预测 */
  forecast: {
    /** 预测收入（元） */
    next7Days: number;
    /** 预测置信度（0-1） */
    confidence: number;
  };
}

// ============================================
// 增长分析
// ============================================

/**
 * 增长分析
 *
 * 客户 / 内容 / 受众三维增长
 */
export interface GrowthAnalysis {
  /** 客户增长 */
  customerGrowth: {
    /** 新增客户数 */
    new: number;
    /** 流失客户数 */
    churned: number;
    /** 净增长 */
    net: number;
    /** 增长率（百分比） */
    rate: number;
  };
  /** 内容增长 */
  contentGrowth: {
    /** 当日发布数 */
    published: number;
    /** 总浏览量 */
    totalViews: number;
    /** 新增粉丝数 */
    newFollowers: number;
    /** 互动率（百分比） */
    engagementRate: number;
  };
  /** 受众增长（按平台） */
  audienceGrowth: {
    /** 平台 → 新增粉丝数 */
    byPlatform: Record<string, number>;
  };
}

// ============================================
// 内容分析
// ============================================

/**
 * 内容分析
 *
 * 识别爆款 / 低效 / 给出建议
 */
export interface ContentAnalysis {
  /** 当日发布内容列表 */
  publishedToday: {
    /** 平台 */
    platform: string;
    /** 标题 */
    title: string;
    /** 浏览量 */
    views: number;
    /** 点赞数 */
    likes: number;
    /** 评论数 */
    comments: number;
  }[];
  /** 爆款内容（表现最好的） */
  topPerforming: {
    platform: string;
    title: string;
    /** 关键指标名（如 views / likes / comments） */
    metric: string;
    /** 指标值 */
    value: number;
  }[];
  /** 低效内容（表现不达预期的） */
  underperforming: {
    platform: string;
    title: string;
    /** 低效原因分析 */
    reason: string;
  }[];
  /** 内容建议（如：停止知乎、增加公众号、AI 课程涨价） */
  contentSuggestions: string[];
}

// ============================================
// 客户分析
// ============================================

/**
 * 客户分析
 *
 * 流失风险 / 成交机会 / 跟进
 */
export interface CustomerAnalysis {
  /** 活跃客户数 */
  active: number;
  /** 当日新增客户数 */
  newToday: number;
  /** 流失风险客户 */
  stale: {
    /** 数量 */
    count: number;
    /** 名单 */
    list: {
      /** 客户名 */
      name: string;
      /** 距上次联系天数 */
      daysSinceContact: number;
      /** 预估价值（元） */
      estimatedValue: number;
    }[];
  };
  /** 即将成交的客户 */
  closingDeals: {
    /** 数量 */
    count: number;
    /** 名单 */
    list: {
      /** 客户名 */
      name: string;
      /** 销售阶段 */
      stage: string;
      /** 预估成交金额（元） */
      estimatedValue: number;
      /** 下一步行动 */
      nextAction: string;
    }[];
  };
  /** 需要跟进的客户 */
  needsFollowUp: {
    /** 数量 */
    count: number;
    /** 名单 */
    list: {
      /** 客户名 */
      name: string;
      /** 跟进原因 */
      reason: string;
      /** 建议行动 */
      suggestedAction: string;
    }[];
  };
}

// ============================================
// 风险分析
// ============================================

/**
 * 风险项
 */
export interface RiskItem {
  /** 风险类别 */
  category: 'customer' | 'revenue' | 'content' | 'operational' | 'technical';
  /** 风险描述 */
  description: string;
  /** 影响（如不处理会发生什么） */
  impact: string;
  /** 发生概率 */
  probability: 'high' | 'medium' | 'low';
  /** 缓解措施 */
  mitigation: string;
}

/**
 * 风险分析（按严重程度分级）
 */
export interface RiskAnalysis {
  /** 高风险（需立即处理） */
  high: RiskItem[];
  /** 中风险（本周处理） */
  medium: RiskItem[];
  /** 低风险（持续观察） */
  low: RiskItem[];
}

// ============================================
// Agent 表现分析
// ============================================

/**
 * Agent 表现分析
 */
export interface AgentPerformanceAnalysis {
  /** 总 Agent 数 */
  totalAgents: number;
  /** 当日活跃 Agent 数 */
  activeToday: number;
  /** 各 Agent 表现明细 */
  byAgent: {
    /** Agent ID */
    agentId: string;
    /** Agent 名称 */
    name: string;
    /** 完成任务数 */
    tasksCompleted: number;
    /** 成功率（0-1） */
    successRate: number;
    /** 平均耗时（毫秒） */
    avgDuration: number;
    /** 评分（0-5） */
    rating: number;
  }[];
  /** 表现不佳的 Agent */
  underperforming: {
    agentId: string;
    name: string;
    /** 问题原因 */
    issue: string;
  }[];
}

// ============================================
// Workflow 性能分析
// ============================================

/**
 * Workflow 性能分析
 */
export interface WorkflowPerformanceAnalysis {
  /** 总工作流数 */
  totalWorkflows: number;
  /** 当日执行次数 */
  executedToday: number;
  /** 各 Workflow 表现明细 */
  byWorkflow: {
    /** Workflow ID */
    workflowId: string;
    /** Workflow 名称 */
    name: string;
    /** 执行次数 */
    executions: number;
    /** 成功率（0-1） */
    successRate: number;
    /** 平均耗时（毫秒） */
    avgDuration: number;
    /** 是否已应用优化 */
    optimizationApplied: boolean;
  }[];
  /** 瓶颈分析 */
  bottlenecks: {
    workflowId: string;
    /** 卡点步骤 ID */
    stepId: string;
    /** 问题原因 */
    issue: string;
  }[];
}

// ============================================
// CEO 建议
// ============================================

/**
 * CEO 建议（可执行、有预期收益）
 *
 * 例：
 *   - 停止知乎（content, P2, this_week）
 *   - 增加公众号（content, P1, immediate）
 *   - AI 课程涨价（revenue, P1, this_week）
 *   - 联系客户A（customer, P0, immediate）
 *   - 重新包装 SKU（product, P2, this_month）
 */
export interface CEORecommendation {
  /** 建议 ID */
  id: string;
  /** 优先级（P0 最高 / P3 最低） */
  priority: 'P0' | 'P1' | 'P2' | 'P3';
  /** 类别 */
  category: 'revenue' | 'content' | 'customer' | 'product' | 'operations' | 'growth';
  /** 标题（简短描述） */
  title: string;
  /** 详细描述 */
  description: string;
  /** 预期影响（如：增加收入 ¥5000/月） */
  expectedImpact: string;
  /** 实施工作量 */
  effort: 'low' | 'medium' | 'high';
  /** 时间线 */
  timeline: 'immediate' | 'this_week' | 'this_month';
  /** 具体行动项 */
  actionItems: string[];
  /** 关联实体（如客户 ID / SKU / Workflow） */
  relatedEntities?: {
    /** 实体类型 */
    type: string;
    /** 实体 ID */
    id: string;
    /** 实体名称 */
    name: string;
  }[];
}

// ============================================
// 次日计划
// ============================================

/**
 * 次日计划
 */
export interface NextDayPlan {
  /** 优先级任务（Top 3） */
  priorities: {
    /** 优先级（1 最高） */
    priority: 1 | 2 | 3;
    /** 任务描述 */
    task: string;
    /** 负责人 */
    owner: string;
    /** 截止时间 */
    deadline: string;
  }[];
  /** 内容发布计划 */
  contentSchedule: {
    /** 平台 */
    platform: string;
    /** 主题 */
    topic: string;
    /** 发布时间 */
    time: string;
  }[];
  /** 客户行动 */
  customerActions: {
    /** 客户名 */
    customer: string;
    /** 行动 */
    action: string;
    /** 原因 */
    reason: string;
  }[];
}

// ============================================
// CEO 日报（完整）
// ============================================

/**
 * CEO 日报（完整结构）
 *
 * 每晚 22:00 自动生成，归档到 Obsidian
 */
export interface CEOReport {
  /** 日报 ID */
  id: string;
  /** 日报日期（YYYY-MM-DD） */
  date: string;
  /** 生成时间（ISO） */
  generatedAt: string;
  /** 报告周期 */
  period: 'daily' | 'weekly' | 'monthly';
  /** 执行摘要 */
  executive: ExecutiveSummary;
  /** 收入分析 */
  revenue: RevenueAnalysis;
  /** 增长分析 */
  growth: GrowthAnalysis;
  /** 内容分析 */
  content: ContentAnalysis;
  /** 客户分析 */
  customer: CustomerAnalysis;
  /** 风险分析 */
  risk: RiskAnalysis;
  /** Agent 表现分析 */
  agentPerformance: AgentPerformanceAnalysis;
  /** Workflow 性能分析 */
  workflowPerformance: WorkflowPerformanceAnalysis;
  /** 可执行建议 */
  recommendations: CEORecommendation[];
  /** 次日计划 */
  nextDay: NextDayPlan;
  /** Obsidian 文件路径（保存后填充） */
  filePath?: string;
  /** 元数据 */
  metadata: {
    /** 数据点数量 */
    dataPoints: number;
    /** 分析深度 */
    analysisDepth: 'overview' | 'detailed' | 'deep';
    /** 使用的 LLM 模型（如降级到规则则为 undefined） */
    llmModel?: string;
  };
}

// ============================================
// CEO 查询/命令枚举
// ============================================

/**
 * CEO 查询类型
 */
export enum CEOQuery {
  /** 获取指定日期的日报 */
  GetReport = 'get_report',
  /** 列出最近的日报 */
  ListReports = 'list_reports',
  /** 获取最新日报 */
  GetLatest = 'get_latest',
  /** 获取统计（如生成次数 / 平均耗时） */
  GetStats = 'get_stats',
}

/**
 * CEO 命令类型
 */
export enum CEOCommand {
  /** 生成日报 */
  GenerateReport = 'generate_report',
  /** 批准建议（标记为已采纳） */
  ApproveRecommendation = 'approve_recommendation',
  /** 驳回建议（标记为已忽略） */
  DismissRecommendation = 'dismiss_recommendation',
  /** 设置分析深度 */
  SetAnalysisDepth = 'set_analysis_depth',
}

// ============================================
// 数据收集 Bundle
// ============================================

/**
 * 数据收集 Bundle
 *
 * 由 DataCollector 并行收集所有维度数据
 * 失败的维度返回 null，不阻塞整体流程
 */
export interface CEODataBundle {
  /** 财务数据 */
  finance: any;
  /** 客户数据 */
  customers: any;
  /** 内容数据 */
  content: any;
  /** Workflow 执行数据 */
  workflows: any;
  /** Agent 表现数据 */
  agents: any;
  /** Memory 数据 */
  memory: any;
  /** 知识图谱统计 */
  knowledgeGraph: any;
  /** 日历排期数据 */
  calendar: any;
  /** 收集时间（ISO） */
  collectedAt: string;
}

// ============================================
// 建议状态（用于批准/驳回）
// ============================================

/**
 * 建议状态
 */
export type RecommendationStatus = 'pending' | 'approved' | 'dismissed';

/**
 * 建议状态记录（用于持久化批准/驳回）
 */
export interface RecommendationState {
  /** 日报 ID */
  reportId: string;
  /** 建议 ID */
  recommendationId: string;
  /** 状态 */
  status: RecommendationStatus;
  /** 驳回原因（仅当 status=dismissed） */
  dismissReason?: string;
  /** 操作时间 */
  updatedAt: string;
}

// ============================================
// 统一返回类型
// ============================================

/**
 * CEO 操作统一返回类型
 */
export interface CEOResult<T = unknown> {
  /** 是否成功 */
  success: boolean;
  /** 返回数据 */
  data?: T;
  /** 错误信息 */
  error?: string;
  /** 时间戳 */
  timestamp: string;
}
